/**
 * What the learner did — the third side of the item-type triple, alongside the
 * content shape and the scheme shape. Adding an item type means adding all
 * three plus a scoring function and fixtures; nothing else in the system moves.
 *
 * The union is deliberately untagged. The item's type is already known from
 * `content.json`, and the scorer dispatches on that, so making an asset write a
 * discriminator would only be noise it could get wrong.
 */

export interface ChoiceResponse {
  readonly choiceId: string;
}

/**
 * Everything the learner ticked: `{ choiceIds: ["a", "d"] }`.
 *
 * A set, not a sequence — the scorer sorts before comparing, so ticking `a` then `d` and
 * ticking `d` then `a` are the same answer. An array rather than a `Record<string, boolean>`
 * because the absent case then has one representation instead of two ("missing" and
 * "explicitly false" would grade the same and record differently).
 *
 * `choiceIds`, deliberately one character from `choiceId`. Two shapes that differ only by a
 * plural is a hazard, and the guard below is where it would bite — so it checks the array
 * before it checks anything else, and a test asserts a `choice` response is not mistaken for
 * this one or the reverse.
 */
export interface MultiChoiceResponse {
  readonly choiceIds: readonly string[];
}

export interface TextEntryResponse {
  readonly text: string;
}

/**
 * The sequence the learner ended up with: `{ order: ["c", "a", "d", "b"] }`.
 *
 * A structure rather than a string, which is the whole reason `ordering` exists as a type.
 * Encoding it as `{ text: "CADB" }` validates and scores, but it is all-or-nothing, and a
 * gradebook holding a string cannot ask which position learners get wrong.
 */
export interface OrderingResponse {
  readonly order: readonly string[];
}

/**
 * Which right-hand entry the learner attached to each left-hand one:
 * `{ pairs: { "nile": "africa" } }`.
 *
 * A left the learner left alone is simply **absent** rather than mapped to null, so
 * "unmatched" needs no sentinel — Dot2Dot used `-1` for it, and every consumer had to know
 * that. An empty map is a blank item.
 */
export interface MatchingResponse {
  readonly pairs: { readonly [leftId: string]: string };
}

/**
 * What the learner typed into each entry field, in field order: `{ values: ["2.4", "2.9"] }`.
 *
 * **Strings, not numbers.** The learner typed characters, and turning them into a number is a
 * grading decision: `"2,5"`, `"2.5abc"` and `""` all have to reach the scorer intact to be judged
 * — blank, or wrong — rather than coerced into something on the way. `Number("")` is 0, which
 * would score an empty box as an answer of zero.
 *
 * **Always an array, even for one field**, which is the ordinary case. One shape means one entry
 * in the wire guard below, and the guard is the thing that fails silently.
 *
 * `values`, deliberately not `texts`: one character away from `text` is the same hazard
 * `choiceIds` carries, and this is the file where it would bite.
 */
export interface NumericResponse {
  readonly values: readonly string[];
}

export type ItemResponse =
  | ChoiceResponse
  | MultiChoiceResponse
  | TextEntryResponse
  | OrderingResponse
  | MatchingResponse
  | NumericResponse;

/** Maps an item type to the response shape it expects. */
export type ResponseFor<
  T extends 'choice' | 'multiChoice' | 'textEntry' | 'ordering' | 'matching' | 'numeric',
> = T extends 'choice'
  ? ChoiceResponse
  : T extends 'multiChoice'
    ? MultiChoiceResponse
    : T extends 'ordering'
      ? OrderingResponse
      : T extends 'matching'
        ? MatchingResponse
        : T extends 'numeric'
          ? NumericResponse
          : TextEntryResponse;

/**
 * Is this an item response at all? **The one place that knows every response shape.**
 *
 * The message guard delegates here rather than listing the shapes again, because it did list
 * them again and the cost was exactly what you would expect: `ordering` was added — content
 * shape, scheme shape, response shape, scorer, rules, fixtures — and the wire guard, which
 * knew only `choiceId` and `text`, dropped every ordering report at the frame boundary. The
 * asset scored locally and looked right; the host recorded nothing and scored 0. A silent
 * discard is the worst possible failure here, so there is one list and this is it.
 *
 * `order` must hold strings, checked rather than assumed: this runs on data from another
 * window, and the scorer downstream compares its entries as ids.
 */
export function isItemResponse(value: unknown): value is ItemResponse {
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;

  if (typeof candidate['choiceId'] === 'string') return true;
  if (typeof candidate['text'] === 'string') return true;

  // Checked as an array of strings for the same reason `order` is: this is data from another
  // window, and the scorer downstream compares the entries as choice ids. An EMPTY array
  // passes — nothing ticked is a blank, and only the scorer may decide that (D32); a guard
  // that rejected it here would turn "answered nothing" into "sent nothing", which is the
  // silent-discard failure D39 exists to prevent.
  if (Array.isArray(candidate['choiceIds'])) {
    return candidate['choiceIds'].every((entry) => typeof entry === 'string');
  }

  if (Array.isArray(candidate['order'])) {
    return candidate['order'].every((entry) => typeof entry === 'string');
  }

  // Strings, checked rather than assumed, for the same reason as `order`: this is data from
  // another window, and the scorer downstream parses each entry. A number that arrived as a
  // number would parse and score, which is worse than being rejected — it would mean two
  // response encodings graded the same and only one of them documented.
  if (Array.isArray(candidate['values'])) {
    return candidate['values'].every((entry) => typeof entry === 'string');
  }

  const pairs = candidate['pairs'];
  if (pairs !== undefined) {
    return (
      pairs !== null &&
      typeof pairs === 'object' &&
      !Array.isArray(pairs) &&
      Object.values(pairs).every((entry) => typeof entry === 'string')
    );
  }

  return false;
}

export function isChoiceResponse(response: ItemResponse): response is ChoiceResponse {
  return typeof (response as ChoiceResponse).choiceId === 'string';
}

export function isMultiChoiceResponse(response: ItemResponse): response is MultiChoiceResponse {
  return Array.isArray((response as MultiChoiceResponse).choiceIds);
}

export function isTextEntryResponse(response: ItemResponse): response is TextEntryResponse {
  return typeof (response as TextEntryResponse).text === 'string';
}

export function isOrderingResponse(response: ItemResponse): response is OrderingResponse {
  return Array.isArray((response as OrderingResponse).order);
}

export function isNumericResponse(response: ItemResponse): response is NumericResponse {
  return Array.isArray((response as NumericResponse).values);
}

export function isMatchingResponse(response: ItemResponse): response is MatchingResponse {
  const pairs = (response as MatchingResponse).pairs;
  return pairs !== null && typeof pairs === 'object' && !Array.isArray(pairs);
}
